import { useLayoutEffect, useRef } from 'react';

let lockCount = 0;

/**
 * Hook to prevent body scrolling when a component is mounted.
 * Preserves scrollbar width to prevent layout shift.
 * Uses reference counting to handle multiple simultaneous locks.
 *
 * @param enabled Whether scroll lock is enabled
 *
 * @example
 * ```tsx
 * function Modal({ isOpen }) {
 *   useBodyScrollLock(isOpen);
 *
 *   if (!isOpen) return null;
 *   return <div>Modal content</div>;
 * }
 * ```
 */
export function useBodyScrollLock(enabled: boolean = true) {
   const originalStylesRef = useRef<{ overflow: string; paddingRight: string } | null>(null);

   useLayoutEffect(() => {
      if (!enabled) return;

      if (lockCount === 0) {
         originalStylesRef.current = {
            overflow: document.body.style.overflow,
            paddingRight: document.body.style.paddingRight,
         };

         document.body.style.overflow = 'hidden';
         preventLayoutShift();
      }

      lockCount++;

      return () => {
         lockCount--;

         if (lockCount === 0 && originalStylesRef.current) {
            document.body.style.overflow = originalStylesRef.current.overflow;
            document.body.style.paddingRight = originalStylesRef.current.paddingRight;
            originalStylesRef.current = null;
         }
      };
   }, [enabled]);
}

function preventLayoutShift() {
   const scrollbarWidth = getScrollbarWidth();
   if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
   }
}

function getScrollbarWidth() {
   return window.innerWidth - document.documentElement.clientWidth;
}
